import { lazy } from "react";

// Wrap React.lazy so a failed dynamic import recovers with exactly one full
// page reload. When a new build is deployed, a long-open tab still runs the old
// index.html, which references chunk hashes (…chunk.js) that no longer exist on
// the server — the import 404s (see .htaccess: missing /static/ assets return a
// real 404, not the SPA shell) and React throws a ChunkLoadError. Reloading
// pulls the fresh index.html and its new chunk names, so the route loads on the
// retry.
//
// The sessionStorage flag stops a reload loop when the import keeps failing for
// some other reason (offline, a genuine 5xx): the second failure is rethrown so
// Suspense/the error path can handle it. Any successful import clears the flag
// so a later deploy can trigger its own one-shot reload.
const RETRY_FLAG = "chunk-reload-attempted";

export default function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      const component = await importer();
      try {
        window.sessionStorage.removeItem(RETRY_FLAG);
      } catch (_) {
        // sessionStorage unavailable (private mode, blocked) — ignore.
      }
      return component;
    } catch (error) {
      let alreadyTried = false;
      try {
        alreadyTried = window.sessionStorage.getItem(RETRY_FLAG) === "1";
        window.sessionStorage.setItem(RETRY_FLAG, "1");
      } catch (_) {
        // No sessionStorage: fall through and rethrow rather than risk a loop.
        alreadyTried = true;
      }

      if (!alreadyTried) {
        window.location.reload();
        // Never resolve, so Suspense keeps showing the fallback until the
        // reload takes over the page.
        return new Promise(() => {});
      }

      throw error;
    }
  });
}
